/**
 * The staged re-plan model for the Overdue table's Planned cell: one span per
 * activity, edited as a whole (calendar range picker + slide arrows). How a
 * plan can move depends on the row:
 *
 *   block  — not started: start and finish move together (a start push);
 *            stretching the finish alone is a duration change
 *   finish — started (committed or staged actual start): only the finish
 *            moves (the Expected Finish pin)
 *   point  — milestone: one date, moved via a start push
 *   none   — finished, pin-exempt, or P6-constraint-held
 */
import type { StatusActivity } from "../../../models";
import { iso10 } from "./dates";
import {
  ActivityStatus,
  isMilestoneTaskType,
  isPinExemptTaskType,
} from "../../../utils/p6";

/** A locally staged re-plan — the full effective span (iso10 dates). */
export interface StagedPlan {
  start: string;
  finish: string;
}

export type PlanMode = "block" | "finish" | "point" | "none";

export interface PlanEditability {
  mode: PlanMode;
  /** Why the plan can't move (mode "none") or the start is locked ("finish"). */
  reason?: string;
}

/** The activity's committed plan (iso10). After an applied push these already
 *  reflect the held dates — the push wrote restart/reend and the constraint/pin. */
export function basePlanOf(a: StatusActivity): StagedPlan {
  const start = iso10(a.start);
  const finish = iso10(a.finish) || start;
  return { start: start || finish, finish };
}

/** Whether the row counts as started — committed status/actual OR a locally
 *  staged actual start (the commit applies actuals first, so by the time any
 *  push runs the start is history). THE one definition; the payload builder
 *  and the table both route on it via planEditability. */
export function isStarted(
  a: StatusActivity,
  stagedActualStart?: string,
): boolean {
  return (
    a.status !== ActivityStatus.NotStart ||
    !!stagedActualStart ||
    !!iso10(a.actual_start)
  );
}

export function planEditability(
  a: StatusActivity,
  useExpectEnd: boolean,
  stagedActualStart: string | undefined,
  stagedActualFinish: string | undefined,
): PlanEditability {
  if (stagedActualFinish || iso10(a.actual_finish)) {
    return { mode: "none", reason: "It finished — nothing left to re-plan" };
  }
  const started = isStarted(a, stagedActualStart);
  if (isMilestoneTaskType(a.task_type)) {
    if (started) return { mode: "none", reason: "The milestone happened" };
    if (a.cstr_type && !a.cstr_pushed) {
      return {
        mode: "none",
        reason: "A P6 constraint holds this milestone — clear it in P6 to re-plan",
      };
    }
    return { mode: "point" };
  }
  if (started) {
    if (!useExpectEnd) {
      return { mode: "none", reason: "Expected Finish dates are off for this project" };
    }
    if (isPinExemptTaskType(a.task_type)) {
      return { mode: "none", reason: "This activity type can't hold an expected finish" };
    }
    return {
      mode: "finish",
      reason: "Work has started — its start is history; the calendar moves the finish",
    };
  }
  if (a.cstr_type && !a.cstr_pushed) {
    return {
      mode: "none",
      reason: "A P6 constraint holds this start — clear it in P6 to re-plan",
    };
  }
  return { mode: "block" };
}
